import type { CefrLevel, CreateWordInput, Language, UpdateWordInput, WordState } from "@rmnlng/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiFetch } from "./api";

// Page size for the infinite list. Server accepts up to 200; 50 keeps each
// request small enough to feel instant on a slow connection but large
// enough that most users see the whole dataset in 1–2 pages.
const WORDS_PAGE_SIZE = 50;

export type WordListItem = {
  id: string;
  lemma: string;
  language: Language;
  pos: string | null;
  gender: string | null;
  // CEFR level derived server-side from the word's CEFR tag (A1…C2). `null`
  // if the word has none. Rendered on the right of each list row.
  level: CefrLevel | null;
  updatedAt: string;
  translationCount: number;
  translationsPreview: string[];
  state: WordState;
};

export type WordListTotals = {
  total: number;
  known: number;
  learning: number;
  new: number;
  // CEFR levels that are actually present in the full dataset for this
  // language — drives the filter chip row in WordsList. Canonical order.
  levels: CefrLevel[];
};

export type WordList = {
  items: WordListItem[];
  total: number;
  limit: number;
  offset: number;
  totals: WordListTotals;
};

export type WordRelated = { id: string; lemma: string; language: Language };

export type WordProgressAgg = {
  state: "learning" | "known";
  easeFactor: number;
  repetitions: number;
  intervalDays: number;
  nextReviewAt: string;
  lastReviewedAt: string | null;
};

export type AgreementType = "FOUR_FORMS" | "TWO_FORMS" | "INVARIABLE";

// Inflection paradigm — POS-dependent key set. The InflectionCard on
// WordDetail dispatches on `pos` and reads the keys it expects. No
// exhaustive shape here on purpose; see SCHEMA.md §6.
export type WordForms = Record<string, string>;

export type WordDetail = {
  id: string;
  lemma: string;
  language: Language;
  pos: string | null;
  gender: string | null;
  notes: string | null;
  imageUrl: string | null;
  imageAttribution: string | null;
  // English search query for the on-demand image service (`apps/images`).
  // Web prefers `imageUrl` when set; otherwise builds a URL from this.
  // Server enforces the precedence — when imageUrl is set, imageQuery
  // is null. See `<CardImage>` for the render-time fallback chain.
  imageQuery: string | null;
  pronunciation: string | null;
  plural: string | null;
  articulated: string | null;
  articulatedPlural: string | null;
  // New inflection / grammatical-class metadata. All nullable — most fields
  // only apply to a subset of POS.
  forms: WordForms | null;
  conjugation: number | null; // 1–4 for VERB
  reflexive: boolean | null;
  impersonal: boolean;
  agreementType: AgreementType | null;
  createdAt: string;
  updatedAt: string;
  translations: WordRelated[];
  synonyms: WordRelated[];
  examples: Array<{
    id: string;
    text: string;
    translation: string;
    createdAt: string;
  }>;
  tags: string[];
  progress: WordProgressAgg | null;
};

// Grammar rules related to a given word — returned by
// `GET /words/:id/related-grammar`. Consumed by the "Relevant grammar"
// section on WordDetail.
export type RelatedGrammar = {
  id: string;
  title: string;
  tags: string[];
  matchedBy: "matchers" | "lemma" | "tags";
};

export function useWordRelatedGrammar(id: string | undefined) {
  return useQuery({
    queryKey: ["words", "related-grammar", id ?? ""],
    queryFn: () => apiFetch<RelatedGrammar[]>(`/words/${id}/related-grammar`),
    enabled: !!id,
  });
}

export type WordsListQuery = { q?: string; language?: Language; state?: WordState; tag?: string };

export const wordsKeys = {
  all: ["words"] as const,
  list: (query: WordsListQuery) => ["words", "list", query] as const,
  detail: (id: string) => ["words", "detail", id] as const,
};

export function useWordsList(query: WordsListQuery = {}) {
  return useInfiniteQuery({
    queryKey: wordsKeys.list(query),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (query.q) params.set("q", query.q);
      if (query.language) params.set("language", query.language);
      if (query.state) params.set("state", query.state);
      if (query.tag) params.set("tag", query.tag);
      params.set("limit", String(WORDS_PAGE_SIZE));
      params.set("offset", String(pageParam));
      return apiFetch<WordList>(`/words?${params.toString()}`);
    },
    // When `offset + items.length >= total`, we've drained the dataset and
    // return `undefined` to tell TanStack there's nothing more to load.
    getNextPageParam: (last) => {
      const loaded = last.offset + last.items.length;
      return loaded < last.total ? loaded : undefined;
    },
    placeholderData: (prev) => prev,
  });
}

export function useWord(id: string | undefined) {
  return useQuery({
    queryKey: wordsKeys.detail(id ?? ""),
    queryFn: () => apiFetch<WordDetail>(`/words/${id}`),
    enabled: !!id,
  });
}

export function useCreateWord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWordInput) =>
      apiFetch<WordDetail>("/words", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (word) => invalidateWords(qc, word.id),
  });
}

export function useUpdateWord(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWordInput) =>
      apiFetch<WordDetail>(`/words/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidateWords(qc, id),
  });
}

export function useDeleteWord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/words/${id}`, { method: "DELETE" }),
    onSuccess: (_, id) => invalidateWords(qc, id),
  });
}

function invalidateWords(qc: QueryClient, id: string) {
  qc.invalidateQueries({ queryKey: wordsKeys.all });
  qc.removeQueries({ queryKey: wordsKeys.detail(id) });
  qc.invalidateQueries({ queryKey: ["progress"] });
  qc.invalidateQueries({ queryKey: ["reviews"] });
}
